/**
 * PostingModal — full-screen overlay that hosts <PostingLayout embedded />.
 *
 * Used by DesktopShell so users can open Daily Posting without leaving
 * the desktop. The standalone /posting URL still works untouched —
 * this just gives the desktop a non-disruptive entry point.
 *
 * Layout:
 *   [translucent backdrop]
 *     [modal box ~95vw × 90vh]
 *       [header: 📋 Daily Posting              🖨  ✕]
 *       [scrollable body: <PostingLayout embedded />]
 *
 * Click the backdrop or ✕ to close. Body is scrollable independent of
 * the rest of the desktop.
 */
import PostingLayout from '../../pages/PostingLayout'
import { useSyncStore, syncColor } from '../../stores/sync'
import { usePostingStore } from '../../stores/posting'

interface PostingModalProps {
  onClose: () => void
}

export default function PostingModal({ onClose }: PostingModalProps) {
  const syncStatus = useSyncStore(s => s.status)
  const forcePull = useSyncStore(s => s.forcePull)
  const forcePush = useSyncStore(s => s.forcePush)
  const clearForm = usePostingStore(s => s.clearForm)
  const dotColor = syncColor(syncStatus)

  function handleClear() {
    if (confirm('Clear the entire posting form? This cannot be undone.')) {
      clearForm()
    }
  }

  return (
    <div
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9000,
        padding: 24,
      }}
    >
      <div
        style={{
          background: '#0f1a2e',
          border: '1px solid #1e3a5f',
          borderRadius: 16,
          width: '100%',
          maxWidth: 920,
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 24px 80px rgba(0,0,0,0.65)',
        }}
      >
        {/* Modal header */}
        <div
          style={{
            background: 'linear-gradient(135deg,#1e2d45,#162033)',
            borderBottom: '1px solid #1e3a5f',
            padding: '12px 18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>📋</span>
            <span
              style={{
                color: '#c9a84c',
                fontWeight: 700,
                fontSize: 14,
                letterSpacing: 0.8,
                textTransform: 'uppercase',
              }}
            >
              Daily Posting
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Sync dot */}
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor }} />
            <button
              onClick={() => { forcePull() }}
              title="Pull from cloud"
              style={{
                background: '#0f2a1a',
                border: '1px solid #166534',
                borderRadius: 6,
                color: '#4ade80',
                fontSize: 11,
                fontWeight: 700,
                padding: '5px 12px',
                cursor: 'pointer',
              }}
            >
              ⬇ Pull
            </button>
            <button
              onClick={() => { forcePush() }}
              title="Save & Push"
              style={{
                background: 'linear-gradient(135deg,#c9a84c,#dbb960)',
                border: 'none',
                borderRadius: 6,
                color: '#1a2744',
                fontSize: 11,
                fontWeight: 700,
                padding: '5px 12px',
                cursor: 'pointer',
              }}
            >
              ⬆ Push
            </button>
            <button
              onClick={() => window.print()}
              title="Print"
              style={{
                background: '#7c3aed',
                border: 'none',
                borderRadius: 6,
                color: '#fff',
                fontSize: 11,
                fontWeight: 700,
                padding: '5px 12px',
                cursor: 'pointer',
              }}
            >
              🖨 Print
            </button>
            <button
              onClick={handleClear}
              title="Clear form"
              style={{
                background: '#dc2626',
                border: 'none',
                borderRadius: 6,
                color: '#fff',
                fontSize: 11,
                fontWeight: 700,
                padding: '5px 12px',
                cursor: 'pointer',
              }}
            >
              🗑️ Clear
            </button>
            <button
              onClick={onClose}
              title="Close"
              style={{
                background: '#1e2d45',
                border: '1px solid #1e3a5f',
                borderRadius: 6,
                color: '#94a3b8',
                fontSize: 16,
                fontWeight: 700,
                width: 30,
                height: 30,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Modal body — scrollable */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <PostingLayout embedded />
        </div>
      </div>
    </div>
  )
}
